import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import getAxiosInstance from 'api';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DateRangePicker } from 'rsuite';
import LeftArrow from '../../assets/images/svg/left-arrow.svg';
import toast from 'react-hot-toast';
import { getDateRangeFromQuickOption } from 'utils/common';
import ReactApexChart from 'react-apexcharts';
import KYCDetails from './KYCDetails';

// Styled Components
const StyledTableCell = styled(TableCell)(() => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: '14px',
  fontWeight: '500',
}));

const StyledCard = styled(Card)(() => ({
  padding: '24px',
  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  width: '505px',
}));

const BackButton = styled(Button)(() => ({
  backgroundColor: '#fff',
  color: '#000',
  borderRadius: '50%',
  padding: '4px',
  minWidth: '32px',
  height: '32px',
  '&:hover': { backgroundColor: '#f5f5f5' },
}));

const quickRanges = [
  { label: 'Current Day', value: 'day' },
  { label: 'Current Week', value: 'week' },
  { label: 'Current Month', value: 'month' },
  { label: 'Last 3 Months', value: '3months' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 12 Months', value: '12months' },
  { label: 'Quarter to date', value: 'quarter' },
  { label: 'All Time', value: 'all' },
];

const headCells = [
  { id: 'email', label: 'Email Address' },
  { id: 'name', label: 'Name' },
  { id: 'Company Name', label: 'Company Name' },
  { id: 'Model', label: 'Model' },
  { id: 'uploadType', label: 'Upload Type' },
  { id: 'callDate', label: 'Call Date' },
];

function EnhancedTableHead({ order, orderBy, onRequestSort, id }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  const StyledTableCellHead = styled(TableCell)(() => ({
    backgroundColor: '#F0F0F0',
    color: 'rgba(33, 33, 32, 0.60)',
    border: '1px solid #DEDEDE',
  }));
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          if (
            (headCell.id === 'Company Name' &&
              !(id === 'api' || id === 'dashboard')) ||
            (headCell.id === 'Model' && !(id === 'api' || id === 'dashboard')) ||
            (headCell.id === 'uploadType' && !(id === 'api' || id === 'dashboard'))
          ) {
            return null;
          }
          return (
            <StyledTableCellHead key={headCell.id}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </StyledTableCellHead>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

const CallsDetails = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const isKycRoute = id === 'kyc' || id === 'bvn' || id === 'nin' || id === 'cac';

  const [order, setOrder] = useState('DESC');
  const [orderBy, setOrderBy] = useState('id');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pieConfig, setPieConfig] = useState({
    series: [],
    options: {
      chart: { type: 'pie' },
      labels: [],
      legend: { position: 'bottom' },
      theme: {
        monochrome: {
          enabled: true,
          color: '#FF6543',
          shadeTo: 'light',
          shadeIntensity: 0.65,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: { chart: { width: '100%' }, legend: { position: 'bottom' } },
        },
      ],
    },
  });
  const [loadingChart, setLoadingChart] = useState(false);
  const [partnerIds, setPartnerIds] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);

  const [selectedDate, setSelectedDate] = useState({
    0: moment().startOf('month')._d,
    1: moment()._d,
  });
  useEffect(() => {
    if (id === 'connect') {
      setSelectedDate({
        0: moment('2020-01-01').toDate(),
        1: moment().toDate(),
      });
    }
  }, [id]);
  useEffect(() => {
    // Don't fetch data for KYC routes
    if (isKycRoute) return;
    fetchPieChartData();
  }, [selectedDate, id, isKycRoute]);

  useEffect(() => {
    // Don't fetch data for KYC routes
    if (isKycRoute) return;
    fetchData();
  }, [selectedDate, page, limit, id, selectedPartnerId, isKycRoute]);

  const fetchData = () => {
    const tableInfo = tableName();
    if (!tableInfo?.apiUrl) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const filter = {
      product: 'all',
      type: id === 'api' ? 'api' : id === 'connect' ? 'user' : 'online',
    };
    if (selectedPartnerId !== null) {
      filter.businessPartnerId = selectedPartnerId;
    }

    getAxiosInstance(
      `adminOrchestrator/dashboard/${tableInfo.apiUrl}`,
      'post',
      {
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
        page: page + 1,
        limit: limit,
        sortDirection: order,
        sortBy: 'createdAt',
        filter,
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setCalls(res?.data?.data?.items);
          setTotal(res?.data?.data?.total);
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message))
      .finally(() => setLoading(false));
  };

  const fetchPieChartData = () => {
    const tableInfo = tableName();
    if (!tableInfo?.productName) {
      setLoadingChart(false);
      return;
    }

    setLoadingChart(true);
    getAxiosInstance(
      `adminOrchestrator/dashboard/getActiveProductCountsGroupedByBusinessPartner`,
      'post',
      {
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
        filter: {
          product: tableInfo.productName,
          type: id === 'api' ? 'api' : id === 'connect' ? 'user' : 'online',
        },
      }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          const responseData = res.data?.data || [];

          if (responseData.length === 0) {
            // No data → set blank chart
            setPartnerIds([]);
            setPieConfig({
              series: [0],
              options: {
                ...pieConfig.options,
                labels: ['No Data'],
              },
            });
            return;
          }

          const labels = responseData.map(
            (item) => item.companyName || 'Unknown'
          );
          const series = responseData.map((item) => item.count);
          const ids = responseData.map((item) => item.businessPartnerId);

          setPartnerIds(ids);
          setPieConfig({
            series,
            options: {
              ...pieConfig.options,
              labels,
              chart: {
                type: 'pie',
                events: {
                  dataPointSelection: (event, chartContext, config) => {
                    const clickedIndex = config.dataPointIndex;
                    const partnerId = ids[clickedIndex];
                    setSelectedPartnerId(partnerId);
                  },
                },
              },
            },
          });
        }
      })
      .catch((err) =>
        toast.error(
          err?.response?.data?.message || 'Failed to fetch chart data'
        )
      )
      .finally(() => setLoadingChart(false));
  };

  const tableName = () => {
    switch (id) {
      case 'api':
        return {
          title: 'API Calls',
          apiUrl: 'getActiveProducts',
          productName: 'all',
        };
      case 'dashboard':
        return {
          title: 'Dashboard Calls',
          apiUrl: 'getActiveProducts',
          productName: 'all',
        };
      case 'connect':
        return {
          title: 'Connect Calls',
          apiUrl: 'getActiveProducts',
          productName: null,
        };
      case 'credit-assessment':
        return {
          title: 'Credit Assessment Calls',
          apiUrl: 'getConsentByDateRange',
          productName: 'credit',
        };
      case 'direct-debit':
        return {
          title: 'Direct Debit Calls',
          apiUrl: 'getMandateByDateRange',
          productName: 'direct_debit',
        };
      case 'income-verification':
        return {
          title: 'Income Verification Calls',
          apiUrl: 'getIncomeVerificationByDateRange',
          productName: 'income_verification',
        };
      case 'bvn':
        return {
          title: 'BVN Calls',
          apiUrl: 'getBVNByDateRange',
          productName: 'bvn',
        };
      case 'kyc':
        return {
          title: 'KYC Calls',
          apiUrl: null,
          productName: null,
        };
      default:
        return {
          title: 'Calls',
          apiUrl: null,
          productName: null,
        };
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  // Conditionally render KYC Details or regular calls details
  if (isKycRoute) {
    const routeKycType = id === 'kyc' ? '' : id.toUpperCase();
    return <KYCDetails routeKycType={routeKycType} />;
  }

  return (
    <>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center">
          <BackButton onClick={() => navigate(-1)}>
            <Box component="img" src={LeftArrow} />
          </BackButton>
          <Typography variant="h3" ml={2}>
            {tableName().title}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          {/* Filters */}
          <Stack mr={2}>
            {selectedPartnerId !== null && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setSelectedPartnerId(null)}
              >
                Reset Partner Filter
              </Button>
            )}
          </Stack>
          <Autocomplete
            options={quickRanges}
            getOptionLabel={(option) => option.label}
            sx={{ width: 170, marginRight: '1rem' }}
            onChange={(event, newValue) => {
              if (!newValue) return;
              const range = getDateRangeFromQuickOption(newValue.value);
              if (range) setSelectedDate([range.start, range.end]);
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Quick Range" />
            )}
          />
          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            value={selectedDate}
            onChange={(value) => setSelectedDate({ 0: value[0], 1: value[1] })}
            showOneCalendar
          />
        </Box>
      </Stack>

      {/* Pie Chart */}
      {(id === 'api' || id === 'dashboard') && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <StyledCard>
            {loadingChart ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  height: '300px',
                  alignItems: 'center',
                }}
              >
                <CircularProgress color="secondary" />
              </Box>
            ) : (
              <ReactApexChart
                options={pieConfig.options}
                series={pieConfig.series}
                type="pie"
                height={300}
              />
            )}
          </StyledCard>
        </Box>
      )}

      {/* Table */}
      <Card sx={{ mt: 2 }}>
        {loading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', height: '100px' }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 750, backgroundColor: 'white' }}>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                id={id}
              />
              <TableBody>
                {calls?.length ? (
                  calls.map((row) => (
                    <TableRow hover key={row.name}>
                      <StyledTableCell>
                        {row?.email || row?.userEmail}
                      </StyledTableCell>
                      <StyledTableCell>
                        {row?.name ||
                          row?.userName ||
                          `${row?.firstName} ${row?.lastName}`}
                      </StyledTableCell>
                      {(id === 'api' || id === 'dashboard') && (
                        <StyledTableCell>{row?.companyName}</StyledTableCell>
                      )}
                      {(id === 'api' || id === 'dashboard') && (
                        <StyledTableCell>{row?.accountType}</StyledTableCell>
                      )}
                      {(id === 'api' || id === 'dashboard') && (
                      <StyledTableCell >
                        {row?.uploadType}
                      </StyledTableCell>
                      )}
                      <StyledTableCell>
                        {moment(row?.createdAt).format('MMM DD, YYYY hh:mm A')}
                      </StyledTableCell>
                    </TableRow>
                  ))
                ) : (
                  <></>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={limit}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => setLimit(e.target.value)}
        />
      </Card>
    </>
  );
};

export default CallsDetails;
